import { useEffect, useState, useCallback } from "react";
import { Box, VStack, HStack, Icon } from "@chakra-ui/react";
import { debounce } from "lodash";
import moment from "moment";
import { FiCheckSquare } from "react-icons/fi";
import { V2TasksTableServerSide } from "features/Resources/Task/Components/Table/V2TasksTable";
import TasksFiltersComponent, {
  TasksFilters,
  ViewMode,
  StatusFilterMode,
  SortMode,
} from "features/Resources/Task/Components/TasksFilters";
import AppPage from "layouts/AppPage";
import { Heading18 } from "components/Heading";
import useSideDrawer from "hooks/useSideDrawer";
import { useSearchParams } from "react-router";
import { TaskSummary } from "features/Resources/Task/models";
import ActionSideDrawer from "components/ActionSideDrawer";
import TaskActionModalByQueryParam from "features/Resources/Task/Components/Action/TaskActionModalByQueryParam";

const defaultFilters: TasksFilters = {
  view_mode: "all",
  status_filter: "active",
};

const sortOptions = [
  { label: "Start Date: Oldest First", value: "effective_start_date_asc" },
  { label: "Start Date: Newest First", value: "effective_start_date_desc" },
  { label: "Due Date: Oldest First", value: "due_date_asc" },
  { label: "Due Date: Newest First", value: "due_date_desc" },
];

const defaultSortString = "effective_start_date_asc";

const SEARCH_DEBOUNCE_MS = 250;

export default function TasksPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<TasksFilters>(() => {
    // Initialize filters from URL params
    const initialFilters: TasksFilters = { ...defaultFilters };
    const q = searchParams.get("q");
    const assigneeId = searchParams.get("assignee_id");
    const hasIssues = searchParams.get("has_issues");
    const startDate = searchParams.get("start_date");
    const endDate = searchParams.get("end_date");
    const viewMode = searchParams.get("view_mode") as ViewMode;
    const statusFilter = searchParams.get("status_filter") as StatusFilterMode;
    const status = searchParams.get("status");
    const sort = searchParams.get("sort") as SortMode;
    const stageKey = searchParams.get("stage_key");

    if (q) initialFilters.q = q;
    if (assigneeId) initialFilters.assignee_id = assigneeId;
    if (hasIssues) initialFilters.has_issues = hasIssues;
    if (viewMode) initialFilters.view_mode = viewMode;
    if (statusFilter) initialFilters.status_filter = statusFilter;
    if (status) initialFilters.status = status.split(",");
    if (stageKey) initialFilters.stage_key = stageKey;
    if (sort) initialFilters.sort = sort;
    if (startDate) initialFilters.start_date = startDate;
    if (endDate) initialFilters.end_date = endDate;

    return initialFilters;
  });

  // Debounce the search query into a separate value used by the API. The URL
  // and the input itself update immediately on each keystroke; only the
  // fetched query lags, so typing stays responsive without spamming requests.
  const [debouncedQ, setDebouncedQ] = useState<string | undefined>(filters.q);
  const [pushDebouncedQ] = useState(() =>
    debounce((value: string | undefined) => setDebouncedQ(value), SEARCH_DEBOUNCE_MS),
  );
  useEffect(() => () => pushDebouncedQ.cancel(), [pushDebouncedQ]);
  useEffect(() => {
    pushDebouncedQ(filters.q);
  }, [filters.q, pushDebouncedQ]);

  const handleFiltersChange = useCallback(
    (newFilters: TasksFilters) => {
      setFilters(newFilters);

      // Update URL params
      const params = new URLSearchParams();
      if (newFilters.q) params.set("q", newFilters.q);
      if (newFilters.assignee_id) params.set("assignee_id", newFilters.assignee_id);
      if (newFilters.has_issues) params.set("has_issues", newFilters.has_issues);
      if (newFilters.view_mode) params.set("view_mode", newFilters.view_mode);
      if (newFilters.status_filter) params.set("status_filter", newFilters.status_filter);
      if (newFilters.status && newFilters.status.length > 0) params.set("status", newFilters.status.join(","));
      if (newFilters.stage_key) params.set("stage_key", newFilters.stage_key);
      if (newFilters.sort) params.set("sort", newFilters.sort);
      if (newFilters.view_mode === "week") {
        if (newFilters.start_date) params.set("start_date", newFilters.start_date);
        if (newFilters.end_date) params.set("end_date", newFilters.end_date);
      }

      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    // Set default params for reset
    const params = new URLSearchParams();
    params.set("view_mode", defaultFilters.view_mode!);
    if (defaultFilters.status_filter) {
      params.set("status_filter", defaultFilters.status_filter);
    }
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handleSortChange = useCallback(
    (value: string) => {
      const newFilters = {
        ...filters,
        sort: value || undefined,
      };
      handleFiltersChange(newFilters);
    },
    [filters, handleFiltersChange],
  );

  const { isOpen, onClose, selectedItem, expandClickHandlerByItem } = useSideDrawer<TaskSummary>({
    getItemId: (item) => item?.id,
  });

  // Derive current sort with default
  const currentSort = filters.sort || defaultSortString;

  // Build query string from filters with proper filter and sort params
  const queryString = (() => {
    const params = new URLSearchParams();
    let filterIndex = 0;

    // Add search query (debounced — see SEARCH_DEBOUNCE_MS above)
    if (debouncedQ) {
      params.set("q", debouncedQ);
    }

    // Add is_urgent as top-level param
    const statusFilter = filters.status_filter || "all";
    if (!filters.status && statusFilter === "urgent") {
      params.set("is_urgent", "true");
    }

    // Add has_issues as top-level param
    if (filters.has_issues) {
      params.set("has_issues", filters.has_issues);
    }

    // Add stage_key as top-level param
    if (filters.stage_key) {
      params.set("stage_key", filters.stage_key);
    }

    // Exclude pending new hire journeys (include false and NULL - tasks without a journey)
    params.set(`filter[${filterIndex}][field]`, "new_hire_journeys.pending");
    params.set(`filter[${filterIndex}][operator]`, "!=");
    params.set(`filter[${filterIndex}][value]`, "true");
    filterIndex += 1;

    // Add date range filters only if view_mode is "week"
    if (filters.view_mode === "week") {
      if (filters.start_date) {
        params.set(`filter[${filterIndex}][field]`, "due_at");
        params.set(`filter[${filterIndex}][operator]`, ">=");
        params.set(`filter[${filterIndex}][value]`, filters.start_date);
        filterIndex += 1;
      }

      if (filters.end_date) {
        params.set(`filter[${filterIndex}][field]`, "due_at");
        params.set(`filter[${filterIndex}][operator]`, "<=");
        params.set(`filter[${filterIndex}][value]`, filters.end_date);
        filterIndex += 1;
      }
    }

    // Add assignee filter
    if (filters.assignee_id) {
      params.set(`filter[${filterIndex}][field]`, "assignee_id");
      params.set(`filter[${filterIndex}][operator]`, "=");
      params.set(`filter[${filterIndex}][value]`, filters.assignee_id);
      filterIndex += 1;
    }

    // Add individual status filter if specified (takes precedence over status_filter)
    if (filters.status && filters.status.length > 0) {
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "in");
      filters.status.forEach((status) => {
        params.append(`filter[${filterIndex}][value][]`, status);
      });
      filterIndex += 1;
    }

    if (!filters.status && statusFilter === "active") {
      // Active: active, in_progress, blocked, failed
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "in");
      ["active", "in_progress", "blocked", "failed"].forEach((status) => {
        params.append(`filter[${filterIndex}][value][]`, status);
      });
      filterIndex += 1;
    } else if (!filters.status && statusFilter === "closed") {
      // Closed: completed, skipped
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "in");
      ["completed", "skipped"].forEach((status) => {
        params.append(`filter[${filterIndex}][value][]`, status);
      });
      filterIndex += 1;
    } else if (!filters.status && statusFilter === "upcoming") {
      // Upcoming: upcoming
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "=");
      params.set(`filter[${filterIndex}][value]`, "upcoming");
      filterIndex += 1;
    } else if (!filters.status && statusFilter === "overdue") {
      // Overdue: due_at < today AND status is open (upcoming, active, in_progress, blocked, failed)
      const today = moment().format("YYYY-MM-DD");
      params.set(`filter[${filterIndex}][field]`, "due_at");
      params.set(`filter[${filterIndex}][operator]`, "<");
      params.set(`filter[${filterIndex}][value]`, today);
      filterIndex += 1;

      // Only show overdue tasks that are still open
      params.set(`filter[${filterIndex}][field]`, "status");
      params.set(`filter[${filterIndex}][operator]`, "in");
      ["upcoming", "active", "in_progress", "blocked", "failed"].forEach((status) => {
        params.append(`filter[${filterIndex}][value][]`, status);
      });
      filterIndex += 1;
    }
    // If statusFilter is "all", "urgent", or individual status filter is set, don't add any status_filter
    // Note: "urgent" is handled as a top-level param above

    // Add sort based on currentSort
    if (currentSort === "effective_start_date_asc") {
      params.set("sort[0][sort_by]", "effective_start_date");
      params.set("sort[0][direction]", "asc");
    } else if (currentSort === "effective_start_date_desc") {
      params.set("sort[0][sort_by]", "effective_start_date");
      params.set("sort[0][direction]", "desc");
    } else if (currentSort === "due_date_asc") {
      params.set("sort[0][sort_by]", "due_at");
      params.set("sort[0][direction]", "asc");
      params.set("sort[1][sort_by]", "effective_start_date");
      params.set("sort[1][direction]", "asc");
    } else if (currentSort === "due_date_desc") {
      params.set("sort[0][sort_by]", "due_at");
      params.set("sort[0][direction]", "desc");
      params.set("sort[1][sort_by]", "effective_start_date");
      params.set("sort[1][direction]", "desc");
    }

    return params.toString();
  })();

  return (
    <AppPage>
      <HStack my="4" width="100%" justifyContent="space-between">
        <HStack spacing={2}>
          <Icon as={FiCheckSquare} boxSize={5} color="fg.subtle" />
          <Heading18>Tasks</Heading18>
        </HStack>
      </HStack>
      <Box p={6}>
        <VStack spacing={6} align="stretch">
          <TasksFiltersComponent
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onReset={handleReset}
            currentSort={currentSort}
            defaultSortString={defaultSortString}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
          />

          <V2TasksTableServerSide
            queryString={queryString}
            onExpand={expandClickHandlerByItem}
            showTaskActionButtons
            showJourney
          />
        </VStack>
      </Box>

      <ActionSideDrawer isOpen={isOpen} onClose={onClose} prefixId={selectedItem?.prefixId} />
      <TaskActionModalByQueryParam />
    </AppPage>
  );
}
